/**
 * Agent Context Manager
 *
 * Provides a way for tools to access the calling agent's context for tracing.
 * Uses a simple stack-based approach to track the current agent execution context.
 */
class AgentContextManager {
    constructor() {
        this.contextStack = [];
    }
    /**
     * Push agent context onto the stack (called when agent execution starts)
     */
    pushContext(context) {
        this.contextStack.push(context);
    }
    /**
     * Pop agent context from the stack (called when agent execution ends)
     */
    popContext() {
        this.contextStack.pop();
    }
    /**
     * Get the current agent context (for tools to use)
     */
    getCurrentContext() {
        return this.contextStack.length > 0 ? this.contextStack[this.contextStack.length - 1] : null;
    }
    /**
     * Clear all contexts (cleanup)
     */
    clear() {
        this.contextStack = [];
    }
}
// Singleton instance
const agentContextManager = new AgentContextManager();
/**
 * Get the current agent context (for tools making LLM calls)
 */
export function getCurrentAgentContext() {
    return agentContextManager.getCurrentContext();
}
/**
 * Push agent context (called by agents when execution starts)
 */
export function pushAgentContext(context) {
    agentContextManager.pushContext(context);
}
/**
 * Pop agent context (called by agents when execution ends)
 */
export function popAgentContext() {
    agentContextManager.popContext();
}
export function getKeywordsTracingMetadataForTool(toolName) {
    const context = getCurrentAgentContext();
    if (!context) {
        return undefined;
    }
    const threadIdentifier = context.conversationId || 'default';
    let customerIdentifier = 'anonymous';
    let groupIdentifier;
    try {
        const { authService } = require('@carousel-office/shared');
        if (authService?.getCurrentUser) {
            const user = authService.getCurrentUser();
            customerIdentifier = user?.email || 'anonymous';
            groupIdentifier = user?.companyUuid;
        }
    }
    catch {
        // Auth not available in headless mode — use defaults
    }
    return {
        customer_identifier: customerIdentifier,
        group_identifier: groupIdentifier,
        thread_identifier: threadIdentifier,
        metadata: {
            tool_name: toolName,
            agent_name: context.agentName,
            conversation_id: context.conversationId,
            task_id: context.taskId,
            thread_id: context.threadId,
            deck_id: context.documentId,
            timestamp: new Date().toISOString(),
        },
    };
}
